import * as React from 'react';
import {FC, useEffect, useState} from 'react';
import {Loader} from '@flexe/ui-components';
import {UploadParams} from '../edi-files/EdiFilesInterfaces';
import EdiFilesService from '../edi-files/EdiFilesService';
import {DisplayGenericErrors, GenericErrorDisplay} from '../../shared/GenericErrorDisplay';
import OutboundOrders, {Props, ServiceTypes} from './OutboundOrders';
import OutboundOrdersService from './OutboundOrdersService';
import CreateSingleOrderButton from './buttons/CreateSingleOrderButton';
import BulkCancelOrdersModalButton from './buttons/BulkCancelOrdersModalButton';
import OutboundOrdersTable from './OutboundOrdersTable';
import BulkCreateOrdersModalButton from './buttons/BulkCreateOrdersModalButton';
import {OrdersViewType} from './OutboundOrdersInterfaces';
import OrdersViewTypeButton from './buttons/OrdersViewTypeButton';
import PageSizeInput, {PageSize} from './generic/PageSizeInput';
import OutboundOrdersUploadModal from './OutboundOrdersUploadModal';
import {
  csvCreateFileUploadTypeV1,
  csvCreateFileUploadTypeV2,
  csvCreateHeadersV1,
  csvCreateHeadersV2,
  csvCreateOrderByLotHeaders
} from './helpers/OutboundOrderCsv';
import OutboundOrdersBulkCancelModal from './OutboundOrdersBulkCancelModal';
import {UploadHandlerCallback} from './generic/CsvBulkUploadModalOMS';
import OutboundOrderLinesTable from './OutboundOrderLinesTable';

/***** TODO before release
 * Support the create upload modal
 * Support the lines view
 * Support links/URLs
 *  The existing page generates URL parameters so you can have bookmarked/shared links
 * Support the back button well
 *  Be sure to validate the issue encountered in OMS-16854, which was fixed
 *  by looking for true as a string when loading filters
 * Make sure element alignment and locations are good
 * Make sure low priority bugs like pagination are fixed
 *  Introduce warning if total number of results has changed since initial load
 *  Also reset results if they go back to page 1 (clear tokens)
 * Cancel lines button
 * Add error if API fails and maybe a retry button
 * Move loading the customer configuration to the bulk create modal
 * Feature flagging for reservations (filter and column)
 */

export interface OrdersSearchPageState {
  pageSize: PageSize;
  viewType: OrdersViewType;
  showBulkCancellationModal: boolean;
  showBulkCreateModal: boolean;
  isModalUploading: boolean;
  errors: GenericErrorDisplay[];
}
const initialOrdersSearchPageState: OrdersSearchPageState = {
  pageSize: PageSize.Fifty,
  viewType: OrdersViewType.orders,
  showBulkCancellationModal: false,
  showBulkCreateModal: false,
  isModalUploading: false,
  errors: []
};

export interface OrdersPageError {
  errorType: 'None' | 'UnexpectedFailure' | 'ApiResponse';
  details?: string[];
}

interface CustomerConfiguration {
  carriers: string[];
  serviceTypes: ServiceTypes;
}
const initialCustomerConfiguration = null;
const bulkOrderCreateTemplatePath = '/static/files/OutboundOrderBulkCreateV2.csv';
const orderByLotCreateTemplatePath = 'static/files/OutboundOrderByLotBulkCreate.csv';

export const dateFormat = 'MM/DD/YY h:mma';

async function loadCustomerConfiguration(ediFilesService: EdiFilesService): Promise<CustomerConfiguration> {
  if (!ediFilesService) {
    return initialCustomerConfiguration;
  }

  const data = await ediFilesService.getCarriersAndServiceTypes();
  // this ridiculousness is to remove a ® character from the
  // API-returned value for the carrier UPS
  const cleanedServiceTypes = data.data.serviceTypes;
  /* eslint-disable */
  cleanedServiceTypes['UPS'] = cleanedServiceTypes['UPS®'];
  delete cleanedServiceTypes['UPS®'];
  /* eslint-enable */

  return {
    carriers: data.data.carriers,
    serviceTypes: cleanedServiceTypes
  };
}

// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
const handleBulkCreateUpload = async (_params: UploadParams, type: string, _callback: UploadHandlerCallback) => {
  // TODO
};

const OutboundOrdersV2: FC<Props> = (props) => {
  const [ordersSearchPageState, setOrdersSearchPageState] = useState<OrdersSearchPageState>(
    initialOrdersSearchPageState
  );
  const [customerConfiguration, setCustomerConfiguration] = useState<CustomerConfiguration>(
    initialCustomerConfiguration
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    loadCustomerConfiguration(props.ediFilesService || new EdiFilesService(props.authenticityToken)).then((result) => {
      setCustomerConfiguration(result);
      setIsLoading(false);
    });
  }, []);

  const toggleBulkCreateModal = () =>
    setOrdersSearchPageState({
      ...ordersSearchPageState,
      showBulkCreateModal: !ordersSearchPageState.showBulkCreateModal
    });
  const toggleBulkCancellationModal = () =>
    setOrdersSearchPageState({
      ...ordersSearchPageState,
      showBulkCancellationModal: !ordersSearchPageState.showBulkCancellationModal
    });
  const setViewType = (newViewType: OrdersViewType) => {
    if (newViewType !== ordersSearchPageState.viewType) {
      setOrdersSearchPageState({...ordersSearchPageState, viewType: newViewType});
    }
  };

  const setErrors = (rawError: OrdersPageError) => {
    const errors: GenericErrorDisplay[] = [];
    switch (rawError.errorType) {
      case 'UnexpectedFailure':
        errors.push({
          header: 'Unexpected error. Please check your connection and refresh. Contact Flexe if the error persists.',
          details: rawError.details
        });
        break;

      case 'ApiResponse':
        errors.push({
          header: 'Failed to retrieve data:',
          details: rawError.details
        });
    }

    setOrdersSearchPageState({...ordersSearchPageState, errors});
  };
  const setPageSize = (v: PageSize) => setOrdersSearchPageState({...ordersSearchPageState, pageSize: v});

  // TODO remove all showOldView stuff prior to customer release
  const [showOldView, setShowOldView] = useState<boolean>(false);
  if (showOldView) {
    return <OutboundOrders {...props} />;
  }

  if (isLoading) {
    return (
      <div className="container-fluid fulfillment-index outbound-orders">
        <Loader loading={isLoading} />
      </div>
    );
  }

  const outboundOrdersService = props.outboundOrdersService || new OutboundOrdersService();

  const tableToShow =
    ordersSearchPageState.viewType === OrdersViewType.orders ? (
      <OutboundOrdersTable
        companyId={props.currentCompany.id}
        outboundOrdersService={outboundOrdersService}
        pageSize={ordersSearchPageState.pageSize}
        setErrors={setErrors}
      />
    ) : (
      <OutboundOrderLinesTable
        outboundOrdersService={outboundOrdersService}
        pageSize={ordersSearchPageState.pageSize}
        setErrors={setErrors}
      />
    );

  const distributionByLotShipper = props.distributionByLotReservationIds.length > 0;
  const templatePath = distributionByLotShipper ? orderByLotCreateTemplatePath : bulkOrderCreateTemplatePath;
  const csvHeaders = distributionByLotShipper ? csvCreateOrderByLotHeaders : csvCreateHeadersV2;

  return (
    <div className="container-fluid fulfillment-index outbound-orders">
      <div className="row">
        <div className="col-sm-4">
          <h1>Outbound Orders</h1>
        </div>
        <div className="col-sm-8">
          <BulkCreateOrdersModalButton toggleModal={toggleBulkCreateModal} />
          <a href="/s/fulfillment/ecommerce" className="btn pull-right">
            View Shipments
          </a>
          <CreateSingleOrderButton
            createNewOrdersButtonFlagOn={props.createNewOrdersButtonFlagOn}
            companyId={props.currentCompany.id}
            outboundOrdersService={outboundOrdersService}
            showOmniOrderCreateBtn={props.shipperHasOmniReservation}
          />
          <BulkCancelOrdersModalButton
            outboundOrdersService={props.outboundOrdersService}
            showOrderCancellationByCsvButton={ordersSearchPageState.showBulkCancellationModal}
            toggleModal={toggleBulkCancellationModal}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <button className="btn" onClick={() => setShowOldView(true)}>
            &nbsp; Show Old Page
          </button>
          <br />
          <br />
        </div>
      </div>
      <div id="fulfillment-component">
        <div className="row">
          <div className="col-sm-11">
            <OrdersViewTypeButton viewType={ordersSearchPageState.viewType} setViewType={setViewType} />
          </div>
          <div className="col-1 pull-right">
            <PageSizeInput
              label="Results per Page"
              value={ordersSearchPageState.pageSize}
              onValueChange={setPageSize}
            />
          </div>
        </div>
        {DisplayGenericErrors(ordersSearchPageState.errors)}
        {tableToShow}
      </div>
      <OutboundOrdersUploadModal
        templatePath={templatePath}
        distributionByLotShipper={distributionByLotShipper}
        serviceTypes={customerConfiguration.serviceTypes}
        carriers={customerConfiguration.carriers}
        showUploadModal={ordersSearchPageState.showBulkCreateModal}
        uploading={ordersSearchPageState.isModalUploading}
        headers={csvHeaders}
        fileUploadType={csvCreateFileUploadTypeV2}
        // TODO: delete oldRequiredHeaders once old template has been retired
        oldHeaders={csvCreateHeadersV1}
        oldFileUploadType={csvCreateFileUploadTypeV1}
        handleUpload={handleBulkCreateUpload}
        toggleUploadModal={toggleBulkCreateModal}
      />
      <OutboundOrdersBulkCancelModal
        currentCompany={props.currentCompany}
        showUploadModal={ordersSearchPageState.showBulkCancellationModal}
        toggleUploadModal={toggleBulkCancellationModal}
        authenticityToken={props.authenticityToken}
      />
    </div>
  );
};
export default OutboundOrdersV2;
